import { apiFetch } from './client';
import type { ItemMediaUploadTicket } from './masters';

/**
 * The three-step upload — ask, send, confirm — once, for both clients.
 *
 * <p>
 * <strong>Only the middle step differs between them, so only the middle step is a parameter.</strong>
 * The website sends a browser `File` through `XMLHttpRequest` for its progress bar; the phone sends
 * the picture its camera just wrote to disk. Asking and confirming are the same two calls to our API
 * either way, and they are only correct together: presigning without confirming leaves an object
 * nobody references, and the server refuses a confirmation for bytes that never landed.
 * </p>
 *
 * <p>
 * The send step is handed the signed URL and the content type the server signed into it, and must
 * send exactly that type — the store refuses a mismatch, which is the check standing in for the one
 * our API cannot make, since it never sees the bytes. It must <em>not</em> send the session: the URL
 * is already signed, and a bearer token sent to a third-party host is a credential leaked.
 * </p>
 */

/** What the server needs to know about a file before it will sign somewhere to put it. */
export interface UploadSource {
  name: string;
  type: string;
  size: number;
}

/** Sends the bytes to the signed URL. Rejects with a sentence a person can act on. */
export type SendToStore = (uploadUrl: string, contentType: string) => Promise<void>;

export async function uploadThroughStore<T>(
  basePath: string,
  source: UploadSource,
  send: SendToStore,
  extra?: Record<string, unknown>,
): Promise<T> {
  const ticket = await apiFetch<ItemMediaUploadTicket>(`${basePath}/presign`, {
    method: 'POST',
    body: {
      ...extra,
      fileName: source.name,
      // A browser leaves this empty for a type it does not recognise; the server refuses an unknown
      // type either way, and an empty string produces a clearer message than a guess would.
      contentType: source.type,
      sizeBytes: source.size,
    },
  });

  await send(ticket.uploadUrl, ticket.contentType);

  return apiFetch<T>(basePath, {
    method: 'POST',
    body: { ...extra, storageKey: ticket.storageKey, fileName: source.name },
  });
}

/** Where each gallery lives. The routes, written once. */
export const mediaPaths = {
  design: (itemId: number) => `/api/item/${itemId}/media`,
  garment: (unitId: number) => `/api/item-unit/${unitId}/media`,
  category: (categoryId: number) => `/api/item-category/${categoryId}/photo`,
  /**
   * A garment's condition on a booking — `phase` (Out or In) travels as an extra field on both the
   * ask and the confirm. **Never shrunk**: these are dispute evidence (CLAUDE.md, "Uploads").
   */
  bookingGarment: (bookingId: number, garmentId: number) =>
    `/api/booking/${bookingId}/garment/${garmentId}/photo`,
} as const;

/**
 * What a store's refusal means, in words. It answers in XML, not our problem+json, so its `<Code>`
 * (and for an expired link, its `<Message>`) is read out of the body.
 *
 * <p>
 * <strong>A 403 is not one thing.</strong> It was once worded as an expired link throughout, and on
 * 2026-10-20 that sent every phone upload — refused as `SignatureDoesNotMatch` because iOS sent an
 * empty Content-Type — after a cause that was not there.
 * </p>
 */
export function storeRefusal(status: number, answer = ''): string {
  const code = storeErrorCode(answer);
  const message = /<Message>(.*?)<\/Message>/.exec(answer)?.[1] ?? '';

  if (/expired/i.test(message)) {
    return 'The upload link expired before the file finished sending. Try again.';
  }

  if (code === 'RequestTimeTooSkewed') {
    return 'This device’s clock is wrong, so the upload was refused. Set the date and time to automatic and try again.';
  }

  if (code === 'SignatureDoesNotMatch') {
    return 'The store did not accept the upload’s signature. Try again — if it keeps happening, the app needs updating.';
  }

  return code
    ? `The file could not be uploaded (${status}, ${code}).`
    : `The file could not be uploaded (${status}).`;
}

/** The store's own error code, for a log line — never the whole answer, which echoes the access key id. */
export function storeErrorCode(answer: string): string | undefined {
  return /<Code>(.*?)<\/Code>/.exec(answer)?.[1];
}

/**
 * The longest side a photograph is sent at. A shop's catalogue is looked at on a phone and a
 * counter screen, never printed, and a twelve-megapixel original costs a shop on mobile data four
 * megabytes a garment for detail nobody can see — while 1600 pixels still holds the zari work.
 */
export const photoLongSide = 1600;

/**
 * The size to draw a photograph at before sending it: its own size when its longest side is
 * already within {@link photoLongSide}, otherwise scaled down to it with the shape kept. Null means
 * "send it as it is" — scaling up only makes a bigger file of the same detail.
 */
export function shrunkSize(
  width: number,
  height: number,
): { width: number; height: number } | null {
  const long = Math.max(width, height);

  if (!(long > photoLongSide) || width <= 0 || height <= 0) {
    return null;
  }

  const scale = photoLongSide / long;

  return { width: Math.round(width * scale), height: Math.round(height * scale) };
}

/**
 * The camera size to take a photograph at: the smallest whose longest side reaches
 * {@link photoLongSide}, or the largest there is when none does. Sizes a camera reports by name
 * rather than as `WIDTHxHEIGHT` — iOS offers presets such as `Photo` — are skipped, and with none
 * readable the answer is undefined, which leaves the camera on its own default.
 */
export function choosePictureSize(sizes: readonly string[]): string | undefined {
  const parsed = sizes
    .map((size) => {
      const match = /^(\d+)x(\d+)$/.exec(size.trim());

      return match ? { size, long: Math.max(Number(match[1]), Number(match[2])) } : null;
    })
    .filter((entry): entry is { size: string; long: number } => entry !== null)
    .sort((a, b) => a.long - b.long);

  if (parsed.length === 0) {
    return undefined;
  }

  return (parsed.find((entry) => entry.long >= photoLongSide) ?? parsed[parsed.length - 1])?.size;
}
